import {
  CollectionGroup,
  IBrand,
  IChartedServiceSmartGroup,
  IFeeSchedule,
  IPricedServiceCodeEntry,
  IPricedServiceCodeGroup,
  IServiceSmartGroup,
  IStaffer,
  ITreatmentCategory,
  ITreatmentConfiguration,
  ITreatmentConfigurationRef,
  OrganisationCollection,
  ServiceCodeGroupType,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  WithRef,
  all$,
  asDocRef,
  doc$,
  findParentDocPath,
  subCollection,
  toNamedDocument,
  undeletedQuery,
} from '@principle-theorem/shared';
import {
  compact,
  first,
  groupBy,
  keys,
  mapValues,
  sortBy,
  values,
  without,
  zipWith,
} from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChartedItemConfiguration } from '../charted-item-configuration';
import {
  PricedServiceCodeGroup,
  ServiceCodeGroup,
} from '../service-codes/ada-code-category';
import { ChartedServiceSmartGroup } from '../service-codes/charted-service-smart-group';
import { PricedServiceCodeEntry } from '../service-codes/service-code';
import { ServiceProviderHandler } from '../service-codes/service-provider';

export class TreatmentConfiguration {
  static init(
    overrides?: Partial<ITreatmentConfiguration>
  ): ITreatmentConfiguration {
    return {
      ...ChartedItemConfiguration.init(),
      duration: 0,
      description: '',
      conditions: [],
      checklists: [],
      educationalAids: [],
      packages: [],
      assetRequirements: {
        instruments: [],
        equipment: [],
        consumables: [],
      },
      serviceCodeGroups: [],
      serviceCodeSmartGroups: [],
      automatedNotificationRefs: [],
      generatedTaskRefs: [],
      automatedFormIssueRefs: [],
      ...overrides,
    };
  }

  static getBrandRef(
    config: IReffable<ITreatmentConfiguration>
  ): DocumentReference<IBrand> {
    const brandRefPath = findParentDocPath(
      config.ref.path,
      OrganisationCollection.Brands
    );
    if (!brandRefPath) {
      throw new Error('Brand not found');
    }
    return asDocRef<IBrand>(brandRefPath);
  }

  static col(
    parent: IReffable<IBrand> | IReffable<IStaffer>
  ): CollectionReference<ITreatmentConfiguration> {
    return subCollection<ITreatmentConfiguration>(
      parent.ref,
      CollectionGroup.TreatmentConfigurations
    );
  }

  static all$(
    parent: IReffable<IBrand> | IReffable<IStaffer>
  ): Observable<WithRef<ITreatmentConfiguration>[]> {
    return all$(undeletedQuery(this.col(parent))).pipe(
      map((category) => sortBy(category, ['name']))
    );
  }

  static async all(
    parent: IReffable<IBrand> | IReffable<IStaffer>
  ): Promise<WithRef<ITreatmentConfiguration>[]> {
    const configurations = await Firestore.getDocs(
      undeletedQuery(this.col(parent))
    );
    return sortBy(configurations, ['name']);
  }

  static hasScopedGroups(config: ITreatmentConfiguration): boolean {
    return config.serviceCodeGroups.some((group) => !!group.serviceCodeType);
  }

  static getCombinedServiceCodes(
    config: WithRef<ITreatmentConfiguration>,
    groupTypes: ServiceCodeGroupType[] = [ServiceCodeGroupType.Required],
    feeSchedule?: AtLeast<IFeeSchedule, 'serviceCodeType'>
  ): IPricedServiceCodeEntry[] {
    const nonSmartGroupTypes = without(groupTypes, ServiceCodeGroupType.Smart);
    const serviceCodeGroups = TreatmentConfiguration.getServiceGroups(
      config,
      nonSmartGroupTypes,
      feeSchedule
    );
    const standardCodes = serviceCodeGroups
      .reduce((codes: IPricedServiceCodeEntry[], group) => {
        if (group.type === ServiceCodeGroupType.Exclusive) {
          const selectedCode = first(group.serviceCodes);
          return selectedCode ? [...codes, selectedCode] : codes;
        }
        return [...codes, ...group.serviceCodes];
      }, [])
      .map((serviceCode) =>
        PricedServiceCodeEntry.init({
          ...serviceCode,
          chartedItemOrigin: toNamedDocument(config),
        })
      );

    if (!groupTypes.includes(ServiceCodeGroupType.Smart)) {
      return standardCodes;
    }

    const smartGroupCodes = compact(
      TreatmentConfiguration.getSmartGroups(config).map((group) =>
        first(group.serviceCodes)
      )
    );

    return [...standardCodes, ...smartGroupCodes];
  }

  static getServiceGroups(
    config: WithRef<ITreatmentConfiguration>,
    groupTypes?: ServiceCodeGroupType[],
    feeSchedule?: AtLeast<IFeeSchedule, 'serviceCodeType'>
  ): IPricedServiceCodeGroup[] {
    const scopedGroups = feeSchedule
      ? ServiceCodeGroup.filterByScheduleType(
          config.serviceCodeGroups,
          feeSchedule.serviceCodeType
        )
      : config.serviceCodeGroups;

    return scopedGroups
      .filter((group) => (groupTypes ? groupTypes.includes(group.type) : true))
      .map((group) => {
        const pricedGroup = PricedServiceCodeGroup.fromServiceCodeGroup(
          group,
          config
        );
        const selected = first(pricedGroup.serviceCodes);

        return {
          ...pricedGroup,
          selected: selected?.code,
        };
      });
  }

  static getSmartGroups(
    config: WithRef<ITreatmentConfiguration>
  ): IChartedServiceSmartGroup[] {
    return config.serviceCodeSmartGroups.map((serviceCodeSmartGroup) => {
      const codes = compact(
        serviceCodeSmartGroup.serviceCodes.map((code) =>
          ServiceProviderHandler.resolveServiceCode(
            serviceCodeSmartGroup.serviceCodeType,
            code
          )
        )
      );

      const pricedCodes = compact(codes)
        .map((codeInfo) => ({
          type: serviceCodeSmartGroup.serviceCodeType,
          code: codeInfo.code,
          taxStatus: codeInfo.taxStatus,
          chartedItemOrigin: toNamedDocument(config),
        }))
        .map((pricedCode) => PricedServiceCodeEntry.init(pricedCode));

      const group: IChartedServiceSmartGroup = ChartedServiceSmartGroup.init({
        ...serviceCodeSmartGroup,
        serviceCodes: pricedCodes,
      });

      const selected: IPricedServiceCodeEntry | undefined = first(
        group.serviceCodes
      );
      if (selected) {
        group.selected = selected.code;
      }
      return group;
    });
  }

  static removeSmartGroup(
    smartGroups: IServiceSmartGroup[],
    entry: IServiceSmartGroup
  ): IServiceSmartGroup[] {
    return smartGroups.filter((group) => group.uid !== entry.uid);
  }
}

export function getTreatmentConfigurations$(
  treatmentRefs: ITreatmentConfigurationRef[]
): Observable<WithRef<ITreatmentConfiguration>[]> {
  return combineLatest(
    treatmentRefs.map((treatmentRef) => doc$(treatmentRef.ref))
  );
}

export function getTreatmentRefQuantities(
  treatmentRefs: ITreatmentConfigurationRef[]
): number[] {
  return treatmentRefs.map((treatmentRef) => treatmentRef.quantity);
}

export function treatmentRefsToSumData$(
  treatmentRefs: ITreatmentConfigurationRef[]
): Observable<ITreatmentSumData[]> {
  const quantities = getTreatmentRefQuantities(treatmentRefs);
  return getTreatmentConfigurations$(treatmentRefs).pipe(
    map((treatments) => {
      return zipWith(
        quantities,
        treatments,
        (quantity: number, treatment: WithRef<ITreatmentConfiguration>) => {
          return { quantity, treatment };
        }
      );
    })
  );
}

export interface ITreatmentSumData {
  quantity: number;
  treatment: WithRef<ITreatmentConfiguration>;
}

export interface ITreatmentCategoryGroup {
  category?: WithRef<ITreatmentCategory>;
  treatments: WithRef<ITreatmentConfiguration>[];
}

export function treatmentConfigurationsToGroups(
  categories: WithRef<ITreatmentCategory>[],
  treatments: WithRef<ITreatmentConfiguration>[]
): ITreatmentCategoryGroup[] {
  const categoriesMap: Record<
    string,
    WithRef<ITreatmentCategory>
  > = categories.reduce(
    (acc, category) => ({ ...acc, [category.ref.path]: category }),
    {}
  );
  const categoryKeys = keys(categoriesMap);

  const groups = groupBy(treatments, (treatment) =>
    treatment.category && categoryKeys.includes(treatment.category.path)
      ? treatment.category?.path
      : undefined
  );

  const mapped = mapValues(groups, (items, key) => ({
    category: categoriesMap[key],
    treatments: sortBy(items, 'name'),
  }));

  return values(mapped);
}
